import * as Sentry from "@sentry/react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from "react-router-dom";
import { lazy, Suspense, useState, useEffect } from "react";

import { Toaster } from "sonner";
import { getPathLocale, detectPreferredLocale } from "./lib/locale";
import { UserProvider, useUser } from "./context/UserContext";
import { firebaseAuth } from "./firebase";
import { signOut } from "firebase/auth";
import { AppProvider } from "./context/AppContext";
import SessionManager from "./components/SessionManager";
import DataClearOverlay from "./components/DataClearOverlay";
import "./components/AppLayout.css";

// Auth screens: loaded eagerly so login/signup feel instant
import LoginScreen from "./screens/AuthScreens/LoginScreen.tsx";
import ProfileSetupModal from "./components/ProfileSetupModal.tsx";

// All protected screens: loaded lazily — only fetched when user navigates to them
const MainScreen = lazy(() => import("./screens/MainScreen/MainScreen.tsx"));
const ProfileScreen = lazy(() => import("./screens/ProfileScreen/ProfileScreen.tsx"));
const FolderScreen = lazy(() => import("./screens/FolderScreen"));
const NewFileNoteScreen = lazy(() => import("./screens/NewFileNoteScreen/NewFileNoteScreen.tsx"));
const NoteDetailScreen = lazy(() => import("./screens/NoteDetailScreen/NoteDetailScreen"));
const SubscriptionPlansScreen = lazy(() => import("./screens/SubscriptionPlansScreen/SubscriptionPlansScreen"));
const SettingsScreen = lazy(() => import("./screens/Settings/SettingsScreen"));
const AskHelixaScreen = lazy(() => import("./screens/AskHelixaScreen/AskHelixaScreen"));
const AdminScreen = lazy(() => import("./screens/AdminScreen/AdminScreen"));
const TasksScreen = lazy(() => import("./screens/TasksScreen/TasksScreen"));
const TermsOfServiceScreen = lazy(() => import("./screens/Legal/TermsOfServiceScreen"));
const PrivacyPolicyScreen = lazy(() => import("./screens/Legal/PrivacyPolicyScreen"));
const UsagePolicyScreen = lazy(() => import("./screens/Legal/UsagePolicyScreen"));
const DoNotSellScreen = lazy(() => import("./screens/Legal/DoNotSellScreen"));

// LegalDisclaimerScreen is used as a component/modal within LoginScreen, not as a route

function PageLoader() {
  return (
    <div className="app-page-loader">
      <div className="app-page-spinner" />
    </div>
  );
}

function ErrorFallback({ error }: { error: Error }) {
  return (
    <div className="app-error-fallback">
      <div className="app-error-icon">⚠️</div>
      <h2 className="app-error-title">Something went wrong</h2>
      <p className="app-error-text">{error.message || "Please refresh the page."}</p>
      <div className="app-error-actions">
        <button
          onClick={() => window.location.reload()}
          className="app-error-btn app-error-btn-primary"
        >
          Reload Page
        </button>
      </div>
    </div>
  );
}

function SignOutAndRedirect() {
  useEffect(() => {
    signOut(firebaseAuth).catch(() => {});
  }, []);
  return <PageLoader />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, profileLoading, profileError, userProfile } = useUser();

  if (loading || (user && (profileLoading || profileError))) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (userProfile?.passwordSet === false) return <SignOutAndRedirect />;

  const profileSetupComplete = userProfile?.profileComplete === true;

  // Always derive name parts from displayName as fallback; Firestore data takes priority.
  const authNameParts = (user.displayName ?? "").trim().split(" ");

  return (
    <>
      {children}
      {!profileSetupComplete && (
        <ProfileSetupModal
          prefillFirstName={userProfile?.firstName || authNameParts[0] || ""}
          prefillLastName={userProfile?.lastName || authNameParts.slice(1).join(" ") || ""}
        />
      )}
    </>
  );
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, userProfile, profileLoading } = useUser();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) { setIsAdmin(null); return; }
    setIsAdmin(null);
    user.getIdTokenResult(true).then(r => setIsAdmin(!!r.claims.admin)).catch(() => setIsAdmin(false));
  }, [user]);

  if (loading || (user && profileLoading)) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (userProfile?.passwordSet === false) return <SignOutAndRedirect />;
  if (isAdmin === null) return <PageLoader />;
  if (!isAdmin) return <Navigate to="/main" replace />;
  return <>{children}</>;
}

function RootRoute() {
  const { user, loading } = useUser();

  if (loading) return <PageLoader />;
  return <Navigate to={user ? "/main" : "/login"} replace />;
}

function RootLayout() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  );
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/terms-of-service" element={<TermsOfServiceScreen />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyScreen />} />
      <Route path="/usage-policy" element={<UsagePolicyScreen />} />
      <Route path="/do-not-sell" element={<DoNotSellScreen />} />
      <Route path="/complete-profile" element={<Navigate to="/main" replace />} />
      <Route path="/main" element={<ProtectedRoute><MainScreen /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>} />
      <Route path="/folder/:folderKey" element={<ProtectedRoute><FolderScreen /></ProtectedRoute>} />
      <Route path="/new-note" element={<ProtectedRoute><NewFileNoteScreen /></ProtectedRoute>} />
      <Route
        path="/new-note/:folderKey"
        element={<ProtectedRoute><NewFileNoteScreen /></ProtectedRoute>}
      />
      <Route
        path="/note/:folderKey/:noteId"
        element={<ProtectedRoute><NoteDetailScreen /></ProtectedRoute>}
      />
      <Route
        path="/subscription-plans"
        element={<ProtectedRoute><SubscriptionPlansScreen /></ProtectedRoute>}
      />
      <Route path="/settings" element={<ProtectedRoute><SettingsScreen /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksScreen /></ProtectedRoute>} />
      <Route path="/ask-helixa" element={<ProtectedRoute><AskHelixaScreen /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminScreen /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  ),
  { basename: `/${getPathLocale() ?? detectPreferredLocale()}` }
);

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <ErrorFallback error={error as Error} />
      )}
    >
      <UserProvider>
        <AppProvider>
          <SessionManager>
            <RouterProvider router={router} />
            <DataClearOverlay />
            <Toaster
              position="top-right"
              closeButton
              expand={true}
              richColors={false}
            />
          </SessionManager>
        </AppProvider>
      </UserProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
